import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { VoxloudService } from '../../lib/services/voxloudService';

export interface VoxloudConfigOption {
  config_id: string;
  label: string;
  phone: string;
  org_id: string;
  userName?: string;
  extension?: string;
}

/**
 * Click-to-Call Voxloud: carica UNA volta le configurazioni attive dell'organizzazione
 * e offre `startCall(cfg, numero)`. Estratto da ClickToCallButton per poterlo usare
 * anche nelle liste (una sola lettura per la lista, non una per riga).
 */
export function useVoxloudClickToCall() {
  const [configs, setConfigs] = useState<VoxloudConfigOption[]>([]);
  const [calling, setCalling] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', user.id)
        .single();
      if (!profile?.organization_id || cancelled) return;

      const { data: cfgs } = await supabase
        .from('api_configurations')
        .select('id, configuration')
        .eq('organization_id', profile.organization_id)
        .eq('service_name', 'voxloud')
        .eq('is_active', true);

      if (!cancelled && cfgs && cfgs.length > 0) {
        // Carica anche i mapping per mostrare l'utente associato
        const configIds = cfgs.map(c => c.id);
        const { data: mappings } = await supabase
          .from('voxloud_user_mappings')
          .select('config_id, voxloud_user_name, voxloud_extension')
          .in('config_id', configIds)
          .eq('is_active', true);

        const mappingsByConfig = new Map<string, { name: string; ext: string }>();
        for (const m of mappings || []) {
          if (!mappingsByConfig.has(m.config_id)) {
            mappingsByConfig.set(m.config_id, { name: m.voxloud_user_name || '', ext: m.voxloud_extension || '' });
          }
        }

        if (cancelled) return;
        setConfigs(cfgs.map((c) => {
          const cfg = (c.configuration || {}) as Record<string, unknown>;
          const mapping = mappingsByConfig.get(c.id);
          return {
            config_id: c.id,
            label: (cfg.pbx_label as string) || '',
            phone: (cfg.phone_number as string) || '',
            org_id: profile.organization_id,
            userName: mapping?.name,
            extension: mapping?.ext,
          };
        }));
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const startCall = useCallback(async (cfg: VoxloudConfigOption, phoneNumber: string) => {
    setCalling(true);
    try {
      // Cerca mapping con click_to_call_resource_id per questa config
      const { data: m } = await supabase
        .from('voxloud_user_mappings')
        .select('id')
        .eq('config_id', cfg.config_id)
        .eq('is_active', true)
        .not('click_to_call_resource_id', 'is', null)
        .limit(1)
        .maybeSingle();

      if (!m) {
        toast.error(`Click-to-Call non configurato per ${cfg.label || cfg.phone}. Vai in Impostazioni > Voxloud.`);
        return;
      }

      // Normalizza: se manca prefisso internazionale, aggiungi +39 (Italia)
      const normalized = phoneNumber.startsWith('+') ? phoneNumber : `+39${phoneNumber.replace(/^0+/, '')}`;
      await VoxloudService.makeCall(cfg.config_id, cfg.org_id, m.id, normalized);
      toast.success('Chiamata avviata! Il tuo telefono squillerà a breve.');
    } catch (err: any) {
      toast.error(err.message || 'Errore nell\'avvio della chiamata');
    } finally {
      setCalling(false);
    }
  }, []);

  return { configs, calling, startCall };
}
